/**
 * Tier ladder mirror — the slice of `apps/web/src/lib/utils/featureGating.ts`
 * (plus `deriveSubscription` in `apps/web/src/contexts/AuthContext.tsx`) that
 * the admin panel needs to explain what an account can do and what a layout
 * requires.
 *
 * Admin is a separate build and cannot import the web app, so this is a copy —
 * the same situation as PLAN_PRICES in RevenueDashboardPage. A stale copy
 * doesn't error, it just tells support the wrong thing, so
 * `apps/web/src/lib/__tests__/adminTierLadderParity.test.ts` imports both and
 * fails on any drift. Change featureGating first, then mirror it here.
 *
 * No runtime imports on purpose: the parity test loads this file from the web
 * app's vitest.
 */

import type {
  AircraftClass, BuildingType, GroundZoneKind, ProceduralShape,
  StructurePresetId, SubscriptionTier,
} from '@hangarmax/types'

export type PlanSlug = 'pilot' | 'pro' | 'business'

export type Feature =
  | 'share'
  | 'export_pdf'
  | 'compare'
  | 'custom_box'
  | 'upload_glb'
  | 'co_brand'
  | 'clean_export'
  | 'share_stats'
  | 'premium_catalog'
  | 'export_4k'
  | 'embed'
  | 'my_fleet'
  | 'ground_surfaces'
  | 'ground_markings'
  | 'business_catalog'
  | 'arch_hangar'
  | 'multi_unit_hangar'
  | 'airport_structures'

export const UNLIMITED = -1

export const TIER_LABELS: Record<SubscriptionTier, string> = {
  trial:    'Trial',
  pilot:    'Pilot',
  pro:      'Pro',
  business: 'Business',
  expired:  'Expired',
}

const TIER_RANK: Record<string, number> = { expired: -1, trial: 0, pilot: 1, pro: 2, business: 3 }

export function tierRank(tier: string | null | undefined): number {
  return TIER_RANK[tier ?? ''] ?? 0
}

/** Stored tier → current vocabulary. `internal_access` still carries legacy `hangar_pro`. */
export function normalizeLegacyTier(tier: string | null | undefined): SubscriptionTier {
  if (tier === 'hangar_pro') return 'pro'
  if (tier === 'free') return 'trial'
  if (tier === 'pilot' || tier === 'pro' || tier === 'business' || tier === 'trial' || tier === 'expired') return tier
  return 'trial'
}

/** Admin-only label: always surfaces the grandfather flag, which support needs to see. */
export function adminTierLabel(tier: string | null | undefined, legacyCaps = false): string {
  const label = TIER_LABELS[normalizeLegacyTier(tier)]
  return legacyCaps ? `${label} (Legacy)` : label
}

// ── Features ────────────────────────────────────────────────────────────────

export const CLASS_TIER: Record<AircraftClass, PlanSlug> = {
  piston:           'pilot',
  rotorcraft_light: 'pilot',
  turboprop_single: 'pilot',
  turboprop_multi:  'pro',
  light_jet:        'pro',
  midsize_jet:      'pro',
  large_cabin:      'business',
  rotorcraft_heavy: 'business',
  airliner:         'business',
}

export const FEATURE_TIERS: Record<Feature, PlanSlug> = {
  share:           'pilot',
  custom_box:      'pilot',
  export_pdf:      'pro',
  compare:         'pro',
  upload_glb:      'business',
  co_brand:        'pro',
  clean_export:    'pilot',
  share_stats:     'pro',
  premium_catalog: 'pro',
  export_4k:       'pro',
  embed:           'pro',
  my_fleet:        'pilot',
  ground_surfaces: 'pro',
  ground_markings: 'business',
  business_catalog: 'business',
  arch_hangar:      'pro',
  multi_unit_hangar: 'business',
  airport_structures: 'business',
}

export const FEATURE_LABELS: Record<Feature, string> = {
  share:           'Share links',
  custom_box:      'Custom box items',
  export_pdf:      'PDF export',
  compare:         'Compare mode',
  upload_glb:      'GLB model upload',
  co_brand:        'Co-branded share pages',
  clean_export:    'Clean image exports',
  share_stats:     'Share page analytics',
  premium_catalog: 'Premium aircraft catalog',
  export_4k:       '4K image export',
  embed:           'Embed on your site',
  my_fleet:        'My Fleet (saved aircraft)',
  ground_surfaces: 'Ground surfaces & labels',
  ground_markings: 'FAA airport markings',
  business_catalog: 'Large-cabin jets, heavy rotorcraft & airliners',
  arch_hangar:      'Arch hangars',
  multi_unit_hangar: 'T-hangar rows & multi-bay hangar rows',
  airport_structures: 'Airport buildings',
}

export const GROUND_KIND_FEATURE: Record<GroundZoneKind, Feature> = {
  surface: 'ground_surfaces',
  line:    'ground_markings',
  stamp:   'ground_surfaces',
}

export const BUILDING_TYPE_FEATURE: Record<BuildingType, Feature | null> = {
  box:               null,
  t_hangar:          null,
  arch_hangar:       'arch_hangar',
  single_row:        'multi_unit_hangar',
  standard_t_hangar: 'multi_unit_hangar',
  nested_t_hangar:   'multi_unit_hangar',
}

export const STRUCTURE_PRESET_FEATURE: Record<StructurePresetId, Feature | null> = {
  office:            'airport_structures',
  warehouse:         'airport_structures',
  mro:               'airport_structures',
  fire_station:      'airport_structures',
  gatehouse:         'airport_structures',
  gse_garage:        'airport_structures',
  utility_building:  'airport_structures',
  deicing_station:   'airport_structures',
  fbo:               'airport_structures',
  regional_terminal: 'airport_structures',
  shade_port:        null,
  covered_walkway:   null,
  parking_canopy:    null,
}

export const SHAPE_FEATURE: Partial<Record<ProceduralShape, Feature>> = {
  jetway:                'airport_structures',
  atc_tower:             'airport_structures',
  fuel_tank_vertical:    'airport_structures',
  fuel_tank_horizontal:  'airport_structures',
}

// ── Caps ────────────────────────────────────────────────────────────────────

export const PILOT_BUILDING_AREA_SQFT = 5_000
export const PRO_BUILDING_AREA_SQFT   = 10_000

const SQFT_PER_SQM = 10.7639

export function buildingAreaSqft(widthM: number, depthM: number): number {
  return widthM * depthM * SQFT_PER_SQM
}

export function getMaxBuildingAreaSqft(tier: SubscriptionTier | null, legacyCaps = false): number {
  if (legacyCaps) return UNLIMITED
  switch (tier) {
    case 'business': return UNLIMITED
    case 'pro':      return PRO_BUILDING_AREA_SQFT
    default:         return PILOT_BUILDING_AREA_SQFT
  }
}

export function getMaxLocations(tier: SubscriptionTier): number {
  switch (tier) {
    case 'trial':    return 1
    case 'pilot':    return 5
    case 'pro':      return UNLIMITED
    case 'business': return UNLIMITED
    case 'expired':  return 1
  }
}

export interface LayoutCaps {
  combined: number
  aircraft: number
  items: number
  hangars: number
}

/** `getLayoutCaps` for a signed-in account outside the embed (no expired Mode A/B). */
export function getLayoutCaps(tier: SubscriptionTier, legacyCaps: boolean): LayoutCaps {
  const combined = (limit: number, hangars: number): LayoutCaps =>
    ({ combined: limit, aircraft: UNLIMITED, items: UNLIMITED, hangars })
  const split = (aircraft: number, items: number, hangars: number): LayoutCaps =>
    ({ combined: UNLIMITED, aircraft, items, hangars })
  switch (tier) {
    case 'trial':    return combined(5, 1)
    case 'expired':  return { combined: 0, aircraft: 0, items: 0, hangars: 0 }
    case 'pilot':    return legacyCaps ? split(UNLIMITED, UNLIMITED, 5) : split(5, UNLIMITED, 5)
    case 'pro':      return legacyCaps ? split(UNLIMITED, UNLIMITED, UNLIMITED) : split(10, UNLIMITED, 10)
    case 'business': return split(UNLIMITED, UNLIMITED, UNLIMITED)
  }
}

// ── Legacy Business preview ─────────────────────────────────────────────────

export function isLegacyBusinessPreviewActive(
  tier: SubscriptionTier | null | undefined,
  legacyCaps: boolean,
  previewUntil: string | null | undefined,
  now: number = Date.now(),
): boolean {
  if (tier !== 'pro' || !legacyCaps || !previewUntil) return false
  const until = Date.parse(previewUntil)
  return !Number.isNaN(until) && until > now
}

export function featureTierFor(
  tier: SubscriptionTier,
  legacyCaps: boolean,
  previewUntil: string | null | undefined,
  now: number = Date.now(),
): SubscriptionTier {
  return isLegacyBusinessPreviewActive(tier, legacyCaps, previewUntil, now) ? 'business' : tier
}

// ── What the app resolves for an account ────────────────────────────────────

export interface AccountAccess {
  /** Billing/caps tier — `internal_access` wins over `subscription_tier`. */
  tier: SubscriptionTier
  /** What every feature gate reads (Business during a legacy Pro's preview). */
  featureTier: SubscriptionTier
  source: 'internal_access' | 'subscription'
  legacyCaps: boolean
  previewOpen: boolean
  maxLocations: number
  maxBuildingAreaSqft: number
  layoutCaps: LayoutCaps
}

/** Mirrors `deriveSubscription` for the fields support asks about. */
export function resolveAccountAccess(input: {
  subscriptionTier: string | null | undefined
  internalAccess: string | null | undefined
  legacyCaps: boolean | null | undefined
  previewUntil: string | null | undefined
  now?: number
}): AccountAccess {
  const internalTier = input.internalAccess ? normalizeLegacyTier(input.internalAccess) : undefined
  const tier = internalTier ?? normalizeLegacyTier(input.subscriptionTier)
  const legacyCaps = input.legacyCaps ?? false
  const now = input.now ?? Date.now()
  return {
    tier,
    featureTier: featureTierFor(tier, legacyCaps, input.previewUntil, now),
    source: internalTier ? 'internal_access' : 'subscription',
    legacyCaps,
    previewOpen: isLegacyBusinessPreviewActive(tier, legacyCaps, input.previewUntil, now),
    maxLocations: getMaxLocations(tier),
    maxBuildingAreaSqft: getMaxBuildingAreaSqft(tier, legacyCaps),
    layoutCaps: getLayoutCaps(tier, legacyCaps),
  }
}

/** "∞" for UNLIMITED, grouped digits otherwise. */
export function formatCap(n: number): string {
  return n === UNLIMITED ? '∞' : n.toLocaleString('en-US')
}

/** "10 aircraft · 10 hangars", "5 objects · 1 hangar", or "Read-only". */
export function formatLayoutCaps(caps: LayoutCaps): string {
  const hangars = `${formatCap(caps.hangars)} hangar${caps.hangars === 1 ? '' : 's'}`
  if (caps.combined === 0) return 'Read-only'
  if (caps.combined !== UNLIMITED) return `${caps.combined} objects · ${hangars}`
  return `${formatCap(caps.aircraft)} aircraft · ${hangars}`
}
